import React, { useState } from 'react';
import useSWR from 'swr';
import api, { Document, ChatMessage } from '../services/api';

interface Props {
  onLoadKnowledge?: () => void;
}

const DocumentPanel: React.FC<Props> = ({ onLoadKnowledge }) => {
  const [crawlLoading, setCrawlLoading] = useState(false);
  const [loadLoading, setLoadLoading] = useState(false);
  const [knowledgeLoaded, setKnowledgeLoaded] = useState(false);

  const [chatQuestion, setChatQuestion] = useState('');
  const [chatAnswer, setChatAnswer] = useState('');
  const [chatSources, setChatSources] = useState<ChatMessage['sources']>([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const [showChat, setShowChat] = useState(false);

  // SWR: 文档列表（有缓存立即显示，后台刷新替换）
  const { data: documentsData, isLoading, isValidating, error, mutate } = useSWR<Document[]>(
    ['documents'],
    () => api.getDocuments().then(r => r.data.data || [])
  );
  const documents = documentsData || [];

  const handleCrawl = async () => {
    setCrawlLoading(true);
    try {
      await api.crawlDocuments();
      alert('爬取任务已启动，请稍后刷新查看进度。信件数量较多，可能需要较长时间。');
      setTimeout(() => mutate(), 10000);
    } catch (e) {
      console.error(e);
    } finally {
      setCrawlLoading(false);
    }
  };

  const handleLoadKnowledge = async () => {
    setLoadLoading(true);
    try {
      const res = await api.loadDocuments();
      const count = res.data.data?.doc_count || 0;
      alert(`知识库加载完成，共 ${count} 个文档片段。`);
      setKnowledgeLoaded(true);
      mutate();
      if (onLoadKnowledge) onLoadKnowledge();
    } catch (e) {
      console.error(e);
    } finally {
      setLoadLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('确定要删除这篇文档吗？')) return;
    try {
      await api.deleteDocument(id);
      mutate();
    } catch (e) {
      console.error(e);
    }
  };

  const handleChat = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!chatQuestion.trim() || chatLoading) return;

    setChatLoading(true);
    setChatAnswer('');
    setChatSources([]);
    try {
      const res = await api.chatWithDocuments(chatQuestion);
      const data = res.data;
      setChatAnswer(data.answer || '没有找到相关文档内容。');
      setChatSources(data.sources || []);
      if (data.success && data.answer) {
        setChatHistory(h => [...h, {
          id: Date.now(),
          question: chatQuestion,
          answer: data.answer,
          sources: data.sources || [],
          created_at: new Date().toISOString()
        }]);
      }
      setChatQuestion('');
    } catch (e) {
      console.error(e);
      setChatAnswer('问答失败，请稍后重试。');
    } finally {
      setChatLoading(false);
    }
  };

  const formatContentLength = (len: number) => {
    if (len > 10000) return `${(len / 1000).toFixed(0)}k 字符`;
    return `${len} 字符`;
  };

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ fontSize: '24px', marginBottom: '8px' }}>📄 文档知识库</h2>
        <p style={{ fontSize: '14px', color: '#666', margin: 0 }}>
          巴菲特致股东的信 — 投资智慧问答
        </p>
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <span className="badge badge-success">{documents.length} 封信</span>
          {knowledgeLoaded && (
            <span className="badge badge-info">✓ 知识库已加载</span>
          )}
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={() => mutate()} disabled={isValidating} className="btn btn-secondary btn-sm">
            🔄 刷新
          </button>
          <button onClick={handleCrawl} disabled={crawlLoading} className="btn btn-secondary btn-sm">
            {crawlLoading ? '⏳ 爬取中...' : '📥 爬取信件'}
          </button>
          <button
            onClick={handleLoadKnowledge}
            disabled={loadLoading || documents.length === 0}
            className="btn btn-brand btn-sm"
          >
            {loadLoading ? '⏳ 加载中...' : '📚 加载知识库'}
          </button>
          <button
            onClick={() => setShowChat(s => !s)}
            className={`btn btn-sm ${showChat ? 'btn-brand' : 'btn-secondary'}`}
          >
            {showChat ? '📋 返回列表' : '💬 文档问答'}
          </button>
        </div>
      </div>

      {/* Chat Section */}
      {showChat ? (
        <div className="chat-container">
          <div style={{ padding: '16px 0', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
            <h3 style={{ margin: 0, fontSize: '18px' }}>
              💬 巴菲特投资智慧问答
            </h3>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#666' }}>
              基于 {documents.length} 封致股东信的智能问答
            </p>
          </div>

          <div className="chat-messages">
            {chatHistory.map((msg) => (
              <div key={msg.id} className="chat-message fade-in">
                <div className="chat-message user">
                  <div className="message-content">{msg.question}</div>
                </div>
                <div className="chat-message assistant">
                  <div className="message-content">{msg.answer}</div>
                </div>
                {msg.sources.length > 0 && (
                  <div className="chat-sources" style={{ paddingLeft: '12px' }}>
                    <span style={{ fontSize: '12px', color: '#666', marginRight: '8px' }}>📚 参考：</span>
                    {msg.sources.map((s, i) => (
                      <span key={i} className="badge badge-success" style={{ marginRight: '4px' }}>
                        {s.year || s.title}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {chatLoading && (
              <div className="chat-message assistant fade-in">
                <div className="message-content" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div className="loading-dots">
                    <span></span><span></span><span></span>
                  </div>
                  <span style={{ fontSize: '14px', color: '#666' }}>AI 思考中...</span>
                </div>
              </div>
            )}
          </div>

          <div className="chat-input-container">
            <form onSubmit={handleChat} className="chat-input-wrapper">
              <input
                type="text"
                className="chat-input"
                placeholder="例如：巴菲特如何看待通货膨胀？"
                value={chatQuestion}
                onChange={e => setChatQuestion(e.target.value)}
                disabled={chatLoading}
              />
              <button 
                type="submit" 
                disabled={chatLoading || !chatQuestion.trim()}
                className="btn btn-brand"
                style={{ borderRadius: '9999px', padding: '12px 24px' }}
              >
                {chatLoading ? '⏳' : '➤'}
              </button>
            </form>
          </div>
        </div>
      ) : (
        /* Document List */
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {isLoading ? (
            <div style={{ padding: '48px', textAlign: 'center' }}>
              <div className="loading-spinner" style={{ margin: '0 auto' }}></div>
              <p style={{ marginTop: '12px', color: '#666' }}>加载中...</p>
            </div>
          ) : error && documentsData === undefined ? (
            <div className="empty-state">
              <div className="empty-state-icon">⚠️</div>
              <h3 className="empty-state-title">加载失败</h3>
              <p className="empty-state-desc">请检查网络后点击「刷新」重试</p>
            </div>
          ) : documents.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">📄</div>
              <h3 className="empty-state-title">暂无文档</h3>
              <p className="empty-state-desc">点击「爬取信件」开始导入巴菲特致股东的信</p>
            </div>
          ) : (
            documents.map((doc, index) => (
              <div 
                key={doc.id}
                style={{ 
                  padding: '16px',
                  borderBottom: index < documents.length - 1 ? '1px solid rgba(0,0,0,0.05)' : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '16px'
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h4 style={{ margin: '0 0 4px', fontSize: '15px', fontWeight: 500 }}>
                    {doc.title}
                  </h4>
                  <div style={{ display: 'flex', gap: '12px', fontSize: '13px', color: '#666' }}>
                    <span>📅 {doc.year}年</span>
                    <span>📝 {formatContentLength(doc.content_length)}</span>
                    {doc.source_url && (
                      <a 
                        href={doc.source_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ color: '#18E299' }}
                        onClick={e => e.stopPropagation()}
                      >
                        原文 ↗
                      </a>
                    )}
                  </div>
                </div>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => handleDelete(doc.id)}
                  style={{ color: '#999', fontSize: '18px', padding: '4px 8px' }}
                >
                  ×
                </button>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default DocumentPanel;
